// Runtime motion / perf-lite signal + preference control.
//
// `perf-lite` is a class on <html> that disables decorative FX (see
// performance.css). It used to be decided once at load with no JS-readable
// signal and no way to recover at runtime. This module:
//   - centralizes the auto-detection (matchMedia / saveData / deviceMemory),
//   - layers an explicit user preference on top ('lite' | 'full' | 'auto'),
//     persisted to localStorage 'mafia.fx', with precedence
//     URL ?fx= > stored pref > auto-detect,
//   - exposes `applyPerfLite()` (called from main.tsx before mount AND on every
//     preference change) and a `usePerfLite()` React hook so components
//     (RoleReveal / DeathScene / PhaseFlash / DiscussionTimer) can branch their
//     JS sequences and jump to a static end-state instead of dead air.

import { useSyncExternalStore } from 'react';
import { storageGet, storageRemove, storageSet } from './safeStorage.js';

export type FxPref = 'lite' | 'full' | 'auto';

const FX_KEY = 'mafia.fx';
const FX_EVENT = 'mafia:fx-change';

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

/** Pure auto-detection — coarse pointer + small/low-memory device, saveData,
 *  or an OS reduced-motion request. */
export function shouldUseLiteFx(): boolean {
  if (!hasWindow()) return false;
  const nav = navigator as Navigator & {
    connection?: { saveData?: boolean };
    deviceMemory?: number;
  };
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !!nav.connection?.saveData ||
    (window.matchMedia('(pointer: coarse)').matches &&
      (window.matchMedia('(max-width: 820px)').matches || (nav.deviceMemory ?? 8) <= 4))
  );
}

export function getFxPreference(): FxPref {
  if (!hasWindow()) return 'auto';
  const v = storageGet(FX_KEY);
  return v === 'lite' || v === 'full' ? v : 'auto';
}

/** Resolve the effective lite state with precedence: ?fx= > stored pref > auto. */
export function resolvePerfLite(): boolean {
  if (!hasWindow()) return false;
  const param = new URLSearchParams(window.location.search).get('fx');
  if (param === 'full') return false;
  if (param === 'lite') return true;
  const pref = getFxPreference();
  if (pref === 'full') return false;
  if (pref === 'lite') return true;
  return shouldUseLiteFx();
}

/** Toggle the <html> class to match the resolved state. */
export function applyPerfLite(): void {
  if (!hasWindow()) return;
  document.documentElement.classList.toggle('perf-lite', resolvePerfLite());
}

/** Persist a preference, re-apply the class live, and notify subscribers.
 *  Because all perf-lite styling is pure CSS keyed on the class, this recovers
 *  or engages full FX with zero remount. */
export function setFxPreference(pref: FxPref): void {
  if (!hasWindow()) return;
  if (pref === 'auto') storageRemove(FX_KEY);
  else storageSet(FX_KEY, pref);
  applyPerfLite();
  window.dispatchEvent(new Event(FX_EVENT));
}

function subscribe(cb: () => void): () => void {
  if (!hasWindow()) return () => {};
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onMq = () => {
    applyPerfLite();
    cb();
  };
  window.addEventListener(FX_EVENT, cb);
  mq.addEventListener?.('change', onMq);
  return () => {
    window.removeEventListener(FX_EVENT, cb);
    mq.removeEventListener?.('change', onMq);
  };
}

function snapshot(): boolean {
  return hasWindow() && document.documentElement.classList.contains('perf-lite');
}

/** React hook: true when decorative motion is suppressed. Re-renders on the
 *  preference event and on OS reduced-motion changes. */
export function usePerfLite(): boolean {
  return useSyncExternalStore(subscribe, snapshot, () => false);
}
